import type {
  DrillDownCardElement,
  QueueCardElement,
  TextBlockElement,
} from '../../../demo-elements/types'
import type { DemoChatScript } from '../../../types'

const CLAIMS_QUEUE: QueueCardElement = {
  kind: 'queue-card',
  id: 'claims-queue',
  title: 'Cascade 3PL — Carrier Claims — 28 eligible · $138K recovered YTD',
  kpis: [
    {
      label: 'Eligible to file',
      value: '28',
      sub: '$14,200 recoverable',
      tone: 'warning',
    },
    {
      label: 'Filed this week',
      value: '47',
      sub: '$11,800 pending carrier decision',
      tone: 'neutral',
    },
    {
      label: 'Approved YTD',
      value: '$138K',
      sub: '612 claims · 0 missed windows in 90d',
      tone: 'good',
    },
  ],
  columns: [
    { id: 'brand', label: 'Brand' },
    { id: 'issue', label: 'Issue' },
    { id: 'carrier', label: 'Carrier', compact: true },
    { id: 'value', label: 'Claim', align: 'right', compact: true },
    { id: 'window', label: 'Window left', align: 'right', compact: true },
  ],
  rows: [
    {
      id: 'claim-oakhill-ups',
      status: 'critical',
      emphasize: true,
      cells: {
        brand: 'OakHill Skincare',
        issue: 'Damaged on delivery',
        carrier: 'UPS',
        value: '$890.00',
        window: { value: '8 days', tone: 'critical' },
        status: 'Ready to file',
      },
    },
    {
      id: 'claim-northwind-ups',
      status: 'warning',
      cells: {
        brand: 'Northwind Apparel',
        issue: 'Late guarantee (17 pkgs)',
        carrier: 'UPS',
        value: '$7,950.00',
        window: { value: '14 days', tone: 'warning' },
        status: 'Ready to file',
      },
    },
    {
      id: 'claim-trailmix-usps',
      status: 'warning',
      cells: {
        brand: 'Trailmix Provisions',
        issue: 'Lost · USPS confirmed (4 pkgs)',
        carrier: 'USPS',
        value: '$1,420.00',
        window: { value: '11 days', tone: 'warning' },
        status: 'Ready to file',
      },
    },
    {
      id: 'claim-altitude-usps',
      status: 'good',
      cells: {
        brand: 'Altitude Supplements',
        issue: 'Lost in transit (3 pkgs)',
        carrier: 'USPS',
        value: '$1,140.00',
        window: { value: '11 days', tone: 'warning' },
        status: 'Ready to file',
      },
    },
    {
      id: 'claim-fernweh-fedex',
      status: 'good',
      cells: {
        brand: 'Fernweh Outdoors',
        issue: 'Damaged on delivery',
        carrier: 'FedEx',
        value: '$1,200.00',
        window: { value: '19 days', tone: 'good' },
        status: 'Ready to file',
      },
    },
    {
      id: 'claim-halcyon-fedex',
      status: 'good',
      cells: {
        brand: 'Halcyon Pet',
        issue: 'Lost in transit',
        carrier: 'FedEx',
        value: '$900.00',
        window: { value: '21 days', tone: 'good' },
        status: 'Ready to file',
      },
    },
    {
      id: 'claim-briar-usps',
      status: 'good',
      cells: {
        brand: 'Briar Coffee Roasters',
        issue: 'Lost · USPS confirmed',
        carrier: 'USPS',
        value: '$700.00',
        window: { value: '13 days', tone: 'warning' },
        status: 'Ready to file',
      },
    },
  ],
  footer:
    'Showing 7 clusters covering all 28 eligible claims · $14,200 recoverable. Carriers: UPS (18) · USPS (8) · FedEx (2).',
}

const OAKHILL_CLAIM_DRILL: DrillDownCardElement = {
  kind: 'drill-down-card',
  id: 'claim-oakhill-drill',
  title: 'OakHill Skincare · UPS 1Z442W1234567890 · 6-pack case',
  subtitle: 'UPS Damaged-on-Delivery · $890.00 · 8 days left on filing window',
  facts: [
    { label: 'Delivered', value: 'May 11 · Atlanta MRF', tone: 'neutral' },
    { label: 'Order', value: '$845.00 + ship $44.85' },
    {
      label: 'Damage',
      value: 'Crushed, contents leaked (6 bottles, 8oz)',
      tone: 'critical',
    },
    {
      label: 'Evidence',
      value: 'Photos, order, receipt, UPS POD signed damage notation',
      tone: 'good',
    },
    {
      label: 'SOP',
      value: 'UPS damage SOP met — 15-day window applies',
      tone: 'good',
    },
  ],
  primaryAction: {
    label: 'File claim with UPS — $890.00',
    context: 'evidence package attached · decision in 5–7 business days',
    preview:
      'UPS Damage claim for tracking 1Z442W1234567890. Evidence: order detail, paid receipt, full tracking history, 4 damage photos from receiving, signed POD with damage notation. SOP justification: UPS damage claims filed within 15 days of delivery per carrier rules. Confirmation number returned on file.',
    accent: 'file',
  },
}

const BULK_FILE_QUEUE: QueueCardElement = {
  kind: 'queue-card',
  id: 'claims-bulk-file',
  title: 'Bulk-file queue — 27 claims · $13,310',
  kpis: [],
  columns: [
    { id: 'group', label: 'Group' },
    { id: 'count', label: 'Count', align: 'right', compact: true },
    { id: 'carrier', label: 'Carrier', compact: true },
    { id: 'value', label: 'Total', align: 'right', compact: true },
  ],
  rows: [
    {
      id: 'bulk-northwind',
      status: 'warning',
      cells: {
        group: 'Northwind UPS late-guarantee',
        count: '17',
        carrier: 'UPS',
        value: '$7,950.00',
      },
    },
    {
      id: 'bulk-trailmix',
      status: 'warning',
      cells: {
        group: 'Trailmix USPS lost (4 pkgs)',
        count: '4',
        carrier: 'USPS',
        value: '$1,420.00',
      },
    },
    {
      id: 'bulk-altitude',
      status: 'warning',
      cells: {
        group: 'Altitude USPS lost (3 pkgs)',
        count: '3',
        carrier: 'USPS',
        value: '$1,140.00',
      },
    },
    {
      id: 'bulk-fernweh',
      status: 'warning',
      cells: {
        group: 'Fernweh FedEx damaged',
        count: '1',
        carrier: 'FedEx',
        value: '$1,200.00',
      },
    },
    {
      id: 'bulk-halcyon',
      status: 'warning',
      cells: {
        group: 'Halcyon FedEx lost',
        count: '1',
        carrier: 'FedEx',
        value: '$900.00',
      },
    },
    {
      id: 'bulk-briar',
      status: 'warning',
      cells: {
        group: 'Briar USPS lost-mail',
        count: '1',
        carrier: 'USPS',
        value: '$700.00',
      },
    },
  ],
  footer:
    'Each filed with carrier-specific evidence package. Per-claim confirmation logged to ShipHero; Slack #ops-leads gets a ping per claim.',
}

const RECOVERY_PATTERN: TextBlockElement = {
  kind: 'text-block',
  id: 'claims-recovery-pattern',
  text: `**$138K recovered YTD — by carrier and root cause:**
- **USPS** · $58K · mostly lost-in-transit (no-scan > 7 days)
- **UPS** · $52K · damaged on delivery + late guarantee
- **FedEx** · $19K · damaged + lost
- **OnTrac** · $9K · lost (mostly Reno facility)

**USPS-Memphis pattern:** 41 of the last 142 USPS-lost shipments touched that hub. Routing decision input.`,
}

const BEAT1_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'claims-beat1-closer',
  text: "28 eligible across 4 carriers. Top one's filing window closes in 8 days — OakHill UPS damage at $890. **Drill into the OakHill claim.**",
}

const BEAT2_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'claims-beat2-closer',
  text: 'Claim ready to file. UPS confirmation logs to ShipHero on approve.',
}

const BEAT3_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'claims-beat3-closer',
  text: 'Queue ready. Approve to file all 27 in one carrier-aware batch.',
}

/**
 * Cascade 3PL — Demo 4: Carrier Claims Agent.
 *
 * Beat 1: Land on the claims queue. 28 eligible, top one (OakHill UPS
 * damaged-on-delivery, $890, 8 days remaining) emphasized.
 *
 * Beat 2: Drill into the OakHill claim. Pre-assembled evidence package
 * (claim type, photos, POD with damage notation). File-now action.
 *
 * Beat 3: Bulk-file the remaining 27 claims grouped by claim cluster.
 * ~$13,310 recovered in one click.
 *
 * Beat 4: YTD recovery pattern — carrier breakdown + the Memphis-hub
 * pattern invisible at per-claim level.
 */
export const carrierClaims: DemoChatScript = {
  id: 'carrier-claims-agent',
  name: 'Carrier Claims Agent',
  description:
    'Demo 4. Cascade files carrier claims on time with the right evidence — recovering money that 3PLs routinely leave on the table because the filing window closes before anyone gets to it.',
  triggers: [
    'claims',
    'claim',
    'carrier claims',
    'carrier claim',
    'file claims',
    'file a claim',
    'filing window',
    'recovery',
    'recover',
    'money on the floor',
    'money',
    'usps claim',
    'usps claims',
    'fedex claim',
    'fedex claims',
    'ups claim',
    'ups claims',
    'lost packages',
    'lost package',
    'damaged shipment',
    'damaged shipments',
    'claim queue',
    'claims queue',
    'memphis',
    'memphis hub',
  ],
  followUps: [
    {
      triggers: [
        'yes',
        'yeah',
        'yep',
        'sure',
        'ok',
        'okay',
        'go',
        'go ahead',
        'do it',
        'please',
        'continue',
        'next',
        "what's next",
        'whats next',
        'show me',
        'show me more',
        'more',
        'keep going',
        'post-shipment',
        'post shipment',
        'customer service',
        'delays',
      ],
      nextScriptId: 'post-shipment-support',
    },
    {
      triggers: ['agents', 'bulk ship', 'morning', 'labels'],
      nextScriptId: 'bulk-label-agent',
    },
  ],
  events: [
    // ─────────── Beat 1: Claims queue ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Pulling eligible carrier claims...',
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        '28 claims eligible right now. The OakHill UPS damaged-on-delivery at $890 is 8 days from its filing-window cutoff — by far the most urgent.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'list_eligible_claims' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: '28 eligible. Top window closes in 8 days.',
    },
    { type: 'demo-element-add', delayMs: 600, element: CLAIMS_QUEUE },
    { type: 'demo-element-add', delayMs: 700, element: BEAT1_CLOSER },
    { type: 'done', delayMs: 400, title: 'Carrier claims — eligible queue' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2: OakHill claim drill + file ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Pulling claim package for OakHill UPS damage...',
    },
    { type: 'tool-call', delayMs: 300, name: 'fetch_claim_package' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: 'Evidence package assembled. $890.00 claim, 8 days left.',
    },
    { type: 'demo-element-add', delayMs: 600, element: OAKHILL_CLAIM_DRILL },
    {
      type: 'approval',
      delayMs: 700,
      proposal: {
        proposalId: 'demo-cascade-claims-single-file',
        toolName: 'file_carrier_claim',
        actionType: 'claim_filing',
        description:
          'File UPS Damage claim for tracking 1Z442W1234567890 — $890.00 (OakHill Skincare). Evidence package attached: order, receipt, tracking, 4 damage photos, POD with damage notation. Window: 8 days remaining.',
      },
    },
    { type: 'demo-element-add', delayMs: 600, element: BEAT2_CLOSER },
    { type: 'done', delayMs: 400, title: 'OakHill claim — filed' },
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-cascade-claims-single-file',
    },

    // ─────────── Beat 3: Bulk file 27 remaining (auto-fires after approval) ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Assembling evidence packages for the remaining 27 claims...',
    },
    { type: 'tool-call', delayMs: 200, name: 'prepare_bulk_filings' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        'OakHill filed · $890.00 in motion. 27 more eligible — same flow, batched.',
    },
    { type: 'demo-element-add', delayMs: 600, element: BULK_FILE_QUEUE },
    {
      type: 'approval',
      delayMs: 700,
      proposal: {
        proposalId: 'demo-cascade-claims-bulk-file',
        toolName: 'bulk_file_carrier_claims',
        actionType: 'claim_filing',
        description:
          'Bulk-file 27 carrier claims: Northwind UPS late-guarantee 17-pkg ($7,950), Trailmix USPS lost 4-pkg ($1,420), Altitude USPS lost 3-pkg ($1,140), Fernweh FedEx damage ($1,200), Halcyon FedEx lost ($900), Briar USPS lost-mail ($700). Total potential recovery $13,310. Each filed with carrier-specific evidence package; per-claim Slack confirmation to #ops-leads.',
      },
    },
    { type: 'demo-element-add', delayMs: 600, element: BEAT3_CLOSER },
    { type: 'done', delayMs: 400, title: 'Bulk-file queue — ready to commit' },
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-cascade-claims-bulk-file',
    },

    // ─────────── Beat 4: YTD recovery pattern (auto-fires after approval) ───────────
    {
      type: 'status',
      delayMs: 200,
      message: 'Rolling up YTD recovery by carrier...',
    },
    { type: 'tool-call', delayMs: 300, name: 'summarize_claim_recoveries' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: '27 filed · $13,310 in motion. YTD recovery by carrier:',
    },
    { type: 'demo-element-add', delayMs: 600, element: RECOVERY_PATTERN },
    {
      type: 'demo-element-add',
      delayMs: 600,
      element: {
        kind: 'text-block',
        id: 'claims-wrap',
        text: '**Want to see how post-shipment exceptions get handled?**',
      },
    },
    { type: 'done', delayMs: 400, title: 'Carrier claims — YTD pattern' },
  ],
}
